<template>
  <section class="booking-passengers" aria-labelledby="booking-passengers-title" :aria-busy="loading || saving">
    <header class="booking-passengers__header">
      <h2 id="booking-passengers-title">Passengers</h2>
      <span>{{ guestCount }} {{ guestCount === 1 ? 'guest' : 'guests' }}</span>
    </header>
    <app-block-loader v-if="loading && !loaded" :min-height="`${Math.max(guestCount, 1) * (completed ? 300 : 90) + 100}px`" :rows="6">Loading passengers…</app-block-loader>
    <div v-else-if="loadError && !loaded" class="booking-passengers__error" :class="{ 'booking-passengers__error--notice': missingAgeRules }" :role="missingAgeRules ? 'status' : 'alert'">
      <span>{{ loadError }}</span>
      <app-button v-if="!missingAgeRules" theme="dark" size="small" @click.native="loadPassengers">Retry</app-button>
    </div>
    <template v-else>
      <div v-if="loadError" class="booking-passengers__error" :class="{ 'booking-passengers__error--notice': missingAgeRules }" :role="missingAgeRules ? 'status' : 'alert'">
        <span>{{ loadError }}</span>
        <app-button v-if="!missingAgeRules" theme="dark" size="small" :disabled="loading" @click.native="loadPassengers">Retry</app-button>
      </div>
      <div class="booking-passengers__summary">
        <strong>Summary</strong>
        <span v-for="group in composition" :key="group.code" class="booking-passengers__tag" :data-age-type="group.code">
          {{ group.count }} {{ group.count === 1 ? group.singular : group.name }}
        </span>
        <app-button v-if="hasLead && canManageLead && !completed" theme="empty" class="booking-passengers__button booking-passengers__link" :disabled="saving || loading || Boolean(loadError)" @click.native="openEditor()">
          <i class="pi pi-link" aria-hidden="true" /> Link Clients
          <span class="booking-passengers__linked" :data-state="linkState"><i v-if="linkState === 'complete'" class="pi pi-check" aria-hidden="true" /> {{ linkedCount }}/{{ passengers.length }} linked</span>
        </app-button>
      </div>
      <section v-for="group in groups" :key="group.code" class="booking-passengers__group" :data-age-type="group.code" :aria-labelledby="`booking-passengers-${group.code}`">
        <h3 :id="`booking-passengers-${group.code}`"><i class="pi pi-users" aria-hidden="true" /> {{ group.name }} <span>{{ group.passengers.length }}</span></h3>
        <article v-for="passenger in group.passengers" :key="passenger.id" class="booking-passengers__passenger" :aria-label="`Passenger #${passenger.id}`">
          <div class="booking-passengers__person">
            <span class="booking-passengers__icon" aria-hidden="true"><i class="pi pi-user" /></span>
            <div class="booking-passengers__identity">
              <h4>{{ name(passenger) }}</h4>
              <span>{{ group.singular }} · Passenger #{{ passenger.id }}</span>
            </div>
            <app-button v-if="!hasLead" theme="empty" class="booking-passengers__button" :disabled="completed || saving || loading || Boolean(loadError)" :aria-label="`${editLabel(passenger)} · Passenger #${passenger.id}`" @click.native="openEditor(passenger)">
              <i class="pi" :class="editLabel(passenger) === 'Add Details' ? 'pi-plus' : 'pi-pencil'" aria-hidden="true" /> {{ editLabel(passenger) }}
            </app-button>
          </div>
          <dl v-if="hasDetails(passenger)" class="booking-passengers__facts">
            <div v-for="field in facts(passenger)" :key="field.name">
              <dt>{{ field.name }}</dt><dd>{{ empty(field.value) ? '—' : field.value }}</dd>
            </div>
            <div v-if="passenger.carrying_infant_passenger_id" class="booking-passengers__wide"><dt>Carrying infant</dt><dd>{{ companion(passenger.carrying_infant_passenger_id) }}</dd></div>
            <div v-if="code(passenger.age_type) === 'infant' && accompanyingAdult(passenger)" class="booking-passengers__wide"><dt>Accompanying adult</dt><dd>{{ companion(accompanyingAdult(passenger).id) }}</dd></div>
          </dl>
        </article>
      </section>
      <p v-if="!passengers.length" class="booking-passengers__empty">No passengers available.</p>
      <footer class="booking-passengers__assignment">
        <div class="booking-passengers__assignment-row">
          <strong>Passenger Assignment</strong>
          <app-switcher :value="completed" :readonly="saving || loading || Boolean(loadError) || !assignment || assignmentLocked || !canUpdateAssignment" :disabled="saving || loading || Boolean(loadError) || !assignment || assignmentLocked || !canUpdateAssignment" aria-label="Passenger assignment completed" @change="updateAssignment" />
          <span v-if="saving || loading" class="booking-passengers__refresh" role="status" aria-label="Updating passengers"><i class="pi pi-spin pi-spinner" aria-hidden="true" /></span>
          <template v-if="completed">
            <span v-if="assignment.completed_by" class="booking-passengers__actor"><span class="booking-passengers__avatar" aria-hidden="true">{{ actorInitials }}</span>{{ assignment.completed_by.full_name || '—' }}</span>
            <time v-if="assignment.completed_at" :datetime="assignment.completed_at">{{ dateTime(assignment.completed_at) }}</time>
          </template>
        </div>
      </footer>
    </template>
    <passenger-modal :name="modalName" :booking="booking" :passengers="passengers" :passenger="selectedPassenger" :policy="policy" :completed="completed" @saved="loadPassengers" />
  </section>
</template>

<script>
import { getFlightsBookingPassengers, patchFlightsBookingPassengersAssignment } from '@/http'
import PassengerModal from './PassengerModal.vue'
import { code, label, empty, dateTime, hasBookingSalesRole, responseError } from './booking-detail'
import { personalFields, passengerGroups, passengerData, passengerName } from './booking-passengers'

export default {
  name: 'BookingPassengers',
  components: { PassengerModal },
  props: { booking: { type: Object, required: true }, refreshKey: { type: Number, default: 0 } },
  data() {
    return { passengers: [], policy: {}, assignment: this.booking.passengers_assignment || null, loaded: false, loading: true, saving: false, loadError: '', requestId: 0, selectedPassenger: null }
  },
  computed: {
    missingAgeRules() { return this.loadError === 'Flight Booking does not contain passenger age rules.' },
    hasLead() { return Boolean(this.booking.lead) },
    canManageLead() { return hasBookingSalesRole(this.$store.state.userGroups) },
    canUpdateAssignment() { return !this.hasLead || this.canManageLead },
    completed() { return Boolean(this.assignment && this.assignment.completed) },
    composition() { return passengerGroups.map(group => ({ ...group, count: this.booking[group.field] })).filter(group => group.code === 'adult' || group.count > 0) },
    guestCount() { return passengerGroups.reduce((total, group) => total + (Number(this.booking[group.field]) || 0), 0) },
    groups() { return passengerGroups.map(group => ({ ...group, passengers: this.passengers.filter(passenger => code(passenger.age_type) === group.code) })).filter(group => group.passengers.length) },
    linkedCount() { return this.passengers.filter(passenger => passenger.client_id !== null && passenger.client_id !== undefined).length },
    linkState() { return this.linkedCount && this.linkedCount === this.passengers.length ? 'complete' : this.linkedCount ? 'partial' : 'empty' },
    assignmentLocked() { return this.completed && code(this.booking.booking_mode) === 'aerocrs' && ['confirmed', 'ticketed'].includes(code(this.booking.status)) },
    actorInitials() {
      const actor = (this.assignment && this.assignment.completed_by) || {}
      const names = (actor.full_name || [actor.first_name, actor.last_name].filter(Boolean).join(' ')).split(/\s+/).filter(Boolean)
      return names.length ? [names[0][0], names.length > 1 ? names[names.length - 1][0] : ''].join('').toUpperCase() : '—'
    },
    modalName() { return `booking-passengers-${this.booking.id}` }
  },
  watch: {
    refreshKey() {
      this.assignment = this.booking.passengers_assignment || null
      this.loadPassengers()
    }
  },
  created() { this.loadPassengers() },
  beforeDestroy() { this.requestId++ },
  methods: {
    code,
    empty,
    dateTime,
    name(passenger) { return passengerName(passenger, this.hasLead, this.completed) },
    editLabel(passenger) { return passenger.first_name || passenger.last_name ? 'Edit Details' : 'Add Details' },
    hasDetails(passenger) {
      const data = passengerData(passenger, this.hasLead, this.completed)
      return this.hasLead && !this.completed ? Boolean(passenger.client) : personalFields.some(field => !empty(data[field]))
    },
    facts(passenger) {
      const data = passengerData(passenger, this.hasLead, this.completed)
      return [
        { name: 'Gender', value: label(data.gender) },
        { name: 'Date of birth', value: dateTime(data.dob, true) },
        { name: 'Age on departure', value: data.age },
        { name: 'Citizenship', value: label(data.citizenship) },
        { name: 'Passport number', value: data.passport_number },
        { name: 'Issuing country', value: label(data.passport_country) },
        { name: 'Passport expiry', value: dateTime(data.passport_expiration_date, true) },
        { name: 'Phone', value: data.phone },
        { name: 'Email', value: data.email },
        { name: 'AeroCRS Passenger ID', value: passenger.aerocrs_id }
      ]
    },
    accompanyingAdult(passenger) { return this.passengers.find(person => person.carrying_infant_passenger_id === passenger.id) },
    companion(id) {
      const passenger = this.passengers.find(person => person.id === id)
      return passenger ? `${this.name(passenger)} · #${id}` : `Passenger #${id}`
    },
    async openEditor(passenger = null) {
      if (this.completed || this.saving || this.loading || this.loadError || (this.hasLead && !this.canManageLead)) return
      this.selectedPassenger = passenger
      await this.$nextTick()
      this.$modal.show(this.modalName)
    },
    async loadPassengers(options = {}) {
      const requestId = ++this.requestId
      this.loading = true
      this.loadError = ''
      try {
        const { data } = await getFlightsBookingPassengers(this.booking.id, { localErrors: true })
        if (requestId !== this.requestId) return
        this.passengers = data.passengers || []
        this.policy = data.policy || {}
        if (Object.prototype.hasOwnProperty.call(data, 'passengers_assignment') || options.assignment) {
          this.assignment = Object.prototype.hasOwnProperty.call(data, 'passengers_assignment') ? data.passengers_assignment : options.assignment
          this.$emit('assignment-change', this.assignment)
        }
        this.loaded = true
      } catch (error) {
        if (requestId === this.requestId) this.loadError = responseError(error, 'Unable to load passengers')
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },
    async updateAssignment(completed) {
      if (this.saving || this.loading || this.loadError || !this.assignment || this.assignmentLocked || !this.canUpdateAssignment) return
      this.saving = true
      const requestId = this.requestId
      try {
        const { data } = await patchFlightsBookingPassengersAssignment(this.booking.id, { completed })
        if (requestId !== this.requestId) return
        // Apply completion together with the re-read server snapshot.
        await this.loadPassengers({ assignment: data })
      } catch (error) {
        if (requestId === this.requestId) this.$notify({ type: 'error', title: responseError(error) })
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.booking-passengers { min-width: 0; overflow: hidden; border: 1px solid #dce0d6; border-radius: 8px; background: #fff; }
.booking-passengers__header { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 14px 18px; border-bottom: 1px solid #dce0d6; background: #fafafa; }
.booking-passengers__header h2 { margin: 0; font-size: 16px; font-weight: 500; }
.booking-passengers__header > span { color: #62685d; font-size: 14px; }
.booking-passengers__summary { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; padding: 14px 18px; }
.booking-passengers strong { font-weight: 500; }
.booking-passengers__tag { padding: 3px 7px; border-radius: 4px; font-size: 13px; }
[data-age-type='adult'] { --passenger-color: #3265a6; --passenger-soft: #edf4ff; }
[data-age-type='child'] { --passenger-color: #3a765b; --passenger-soft: #edf8f2; }
[data-age-type='infant'] { --passenger-color: #9e477b; --passenger-soft: #fcf1f8; }
.booking-passengers__tag, .booking-passengers__group h3, .booking-passengers__icon { color: var(--passenger-color); background: var(--passenger-soft); }
.booking-passengers__group h3 { display: flex; align-items: center; gap: 8px; margin: 0; padding: 10px 18px; border-left: 3px solid var(--passenger-color); font-size: 14px; font-weight: 500; }
.booking-passengers__group h3 > span { margin-left: auto; }
.booking-passengers__passenger { padding: 18px; border-bottom: 1px solid #dce0d6; }
.booking-passengers__person { display: flex; align-items: center; gap: 10px; }
.booking-passengers__icon, .booking-passengers__avatar { display: inline-flex; align-items: center; justify-content: center; flex: 0 0 28px; width: 28px; height: 28px; border-radius: 50%; }
.booking-passengers__identity { flex: 1; min-width: 0; overflow-wrap: anywhere; }
.booking-passengers__identity h4 { margin: 0 0 4px; font-size: 15px; font-weight: 500; }
.booking-passengers__identity > span { color: #62685d; font-size: 13px; }
.booking-passengers .booking-passengers__button { min-height: 34px; padding: 6px 10px; border: 1px solid #dce0d6; border-radius: 6px; background: #fff; color: #20241d; font-size: 13px; line-height: 1.4; flex-shrink: 0; }
.booking-passengers .booking-passengers__button:hover { background: #fafafa; }
.booking-passengers .booking-passengers__button:disabled { opacity: .6; }
.booking-passengers .booking-passengers__button .pi { font-size: 13px; }
.booking-passengers .booking-passengers__link { margin-left: auto; }
.booking-passengers__linked { padding: 2px 5px; border-radius: 3px; background: #fafafa; color: #62685d; }
.booking-passengers__linked[data-state='partial'] { background: #fff7e6; color: #b8860b; }
.booking-passengers__linked[data-state='complete'] { background: #f0f6e7; color: #527b12; }
.booking-passengers__facts { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px 20px; margin: 18px 0 0; font-size: 14px; }
.booking-passengers__facts dt { margin-bottom: 4px; color: #62685d; }
.booking-passengers__facts dd { margin: 0; overflow-wrap: anywhere; }
.booking-passengers__wide { grid-column: 1 / -1; }
.booking-passengers__assignment { padding: 14px 18px; background: #fafafa; }
.booking-passengers__assignment-row { display: flex; align-items: center; flex-wrap: wrap; gap: 10px 12px; font-size: 14px; }
.booking-passengers__actor { display: flex; align-items: center; gap: 6px; }
.booking-passengers__avatar { flex-basis: 24px; width: 24px; height: 24px; border: 1px solid #dce0d6; background: #f0f6e7; color: #527b12; font-size: 10px; }
.booking-passengers__assignment-row time { color: #62685d; }
.booking-passengers__refresh { display: inline-flex; color: #62685d; }
.booking-passengers__error { display: flex; align-items: center; flex-wrap: wrap; gap: 12px; padding: 18px; color: #b03932; }
.booking-passengers__error--notice { color: $color-text-second; font-size: 13px; }
.booking-passengers__empty { padding: 18px; margin: 0; color: #62685d; }
@media (max-width: 1000px) { .booking-passengers__facts { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 520px) {
  .booking-passengers__facts { grid-template-columns: minmax(0, 1fr); }
  .booking-passengers__person { flex-wrap: wrap; }
  .booking-passengers__identity { flex-basis: calc(100% - 38px); }
  .booking-passengers .booking-passengers__link { margin-left: 0; }
}
</style>
